<script setup lang="ts">
import { AdSlot, AppShell } from '@it-tools/shell'
import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CheapestElsewhere from './components/CheapestElsewhere.vue'
import FamilyFilter from './components/FamilyFilter.vue'
import InstanceList from './components/InstanceList.vue'
import InstanceSummary from './components/InstanceSummary.vue'
import ProviderPicker from './components/ProviderPicker.vue'
import type { SlotId } from './components/types'
import WorkloadForm from './components/WorkloadForm.vue'
import {
  type Catalog,
  type Instance,
  loadCatalog,
  parseInstanceKey,
  type ProviderId,
  PROVIDERS,
} from './model/catalog'
import { costSensitivity, fitNode, type NodeFit, type Workload } from './model/fit'
import type { ProviderOptions } from './model/providers'
import {
  cheapest,
  filterInstances,
  type Interval,
  INTERVAL_HOURS,
  sortFits,
  type SortOrder,
} from './model/ranking'
import {
  DEFAULT_STATE,
  type DesignState,
  readLocationState,
  replaceLocationState,
} from './model/urlState'

const { t } = useI18n()

const provider = ref<ProviderId>(DEFAULT_STATE.provider)
const options = ref<ProviderOptions>(DEFAULT_STATE.options)
const workload = ref<Workload>(DEFAULT_STATE.workload)
const families = ref<readonly string[] | null>(DEFAULT_STATE.families)
const sort = ref<SortOrder>(DEFAULT_STATE.sort)
const interval = ref<Interval>(DEFAULT_STATE.interval)
const primary = ref<string | null>(DEFAULT_STATE.primary)
const secondary = ref<string | null>(DEFAULT_STATE.secondary)
const step = ref(DEFAULT_STATE.step)
/** Which slot a click in the list fills. */
const activeSlot = ref<SlotId>('a')
const shareNotice = ref(false)
const loadFailed = ref(false)

const catalogs = shallowRef<Partial<Record<ProviderId, Catalog>>>({})

async function ensureCatalog(id: ProviderId): Promise<void> {
  if (catalogs.value[id]) return
  try {
    const catalog = await loadCatalog(id)
    catalogs.value = { ...catalogs.value, [id]: catalog }
  } catch {
    loadFailed.value = true
  }
}

function apply(state: DesignState): void {
  provider.value = state.provider
  options.value = state.options
  workload.value = state.workload
  families.value = state.families
  sort.value = state.sort
  interval.value = state.interval
  primary.value = state.primary
  secondary.value = state.secondary
  step.value = state.step
  activeSlot.value = 'a'
}

const state = computed<DesignState>(() => ({
  provider: provider.value,
  options: options.value,
  workload: workload.value,
  families: families.value,
  sort: sort.value,
  interval: interval.value,
  primary: primary.value,
  secondary: secondary.value,
  step: step.value,
}))

// A shared link restores the design; anything it got wrong falls back to defaults
// field by field, so the visitor always lands on a working calculator.
onMounted(async () => {
  const restored = readLocationState()
  if (restored) apply(restored)
  // The visible provider first, then the rest for the cross-cloud comparison.
  await ensureCatalog(provider.value)
  await Promise.all(PROVIDERS.map(ensureCatalog))
})

watch(state, (value) => replaceLocationState(value))

watch(provider, (id) => void ensureCatalog(id))

const catalog = computed(() => catalogs.value[provider.value])

const selectedFamilies = computed<ReadonlySet<string>>(
  () =>
    new Set(
      families.value ?? catalog.value?.families.filter((f) => f.default).map((f) => f.id) ?? [],
    ),
)

const fit = (instance: Instance): NodeFit => fitNode(instance, options.value, workload.value)

const listFits = computed(() =>
  catalog.value
    ? sortFits(
        filterInstances(catalog.value.instances, selectedFamilies.value).map(fit),
        sort.value,
      )
    : [],
)

function findInstance(key: string | null): Instance | undefined {
  const parsed = key === null ? null : parseInstanceKey(key)
  if (!parsed) return undefined
  return catalogs.value[parsed.provider]?.instances.find((i) => i.name === parsed.name)
}

const comparing = computed(() => secondary.value !== null)
const hours = computed(() => INTERVAL_HOURS[interval.value])

const slots = computed(() =>
  (
    [
      ['a', primary.value],
      ['b', comparing.value ? secondary.value : null],
    ] as const
  ).flatMap(([id, key]) => {
    const instance = findInstance(key)
    if (!instance) return []
    const result = fit(instance)
    return [{ id, fit: result, points: costSensitivity(instance, options.value, workload.value) }]
  }),
)

// Burstable and shared-core sizes are left out: their price per pod assumes CPU
// they can only borrow.
const elsewhere = computed(() =>
  PROVIDERS.map((id) => {
    const catalog = catalogs.value[id]
    // `loaded` keeps "still loading" apart from "nothing fits": both have no fit.
    if (!catalog) return { provider: id, loaded: false, fit: undefined }
    const candidates = catalog.instances.filter((i) => !i.burstable).map(fit)
    return { provider: id, loaded: true, fit: cheapest(candidates) }
  }),
)

function changeProvider(id: ProviderId): void {
  provider.value = id
  families.value = null
}

function select(key: string): void {
  if (comparing.value && activeSlot.value === 'b') secondary.value = key
  else primary.value = key
}

function startComparing(): void {
  secondary.value = primary.value
  activeSlot.value = 'b'
}

function stopComparing(): void {
  secondary.value = null
  activeSlot.value = 'a'
}

function compareWith(key: string): void {
  if (primary.value === null) {
    primary.value = key
    return
  }
  secondary.value = key
  activeSlot.value = 'b'
}

function reset(): void {
  apply(DEFAULT_STATE)
}

async function share(): Promise<void> {
  replaceLocationState(state.value)
  try {
    await navigator.clipboard.writeText(window.location.href)
    shareNotice.value = true
    setTimeout(() => (shareNotice.value = false), 2000)
  } catch {
    // Clipboard access can be denied; the URL bar already holds the link.
  }
}

const sources = computed(() => {
  const loaded = PROVIDERS.flatMap((id) => {
    const c = catalogs.value[id]
    return c ? [{ id, region: c.region, retrieved: c.retrieved }] : []
  })
  const regions = loaded
    .map(({ id, region }) =>
      t('sources.region', {
        provider: t(`provider.short.${id}`),
        region: region === 'all' ? t('sources.allRegions') : region,
      }),
    )
    .join(', ')
  const date = loaded.map((c) => c.retrieved).sort()[0] ?? ''
  return loaded.length ? t('sources.prices', { regions, date }) : ''
})
</script>

<template>
  <AppShell tool-name-key="tools.kubernetesInstanceCalculator">
    <template #controls>
      <span v-if="shareNotice" class="notice" role="status">{{ t('common.copied') }}</span>
    </template>

    <div class="layout">
      <div class="layout__main">
        <p class="lead">{{ t('intro') }}</p>

        <section class="panel" aria-labelledby="workload-heading">
          <h2 id="workload-heading" class="visually-hidden">{{ t('workload.heading') }}</h2>
          <WorkloadForm v-model="workload" />
          <div class="panel__actions">
            <button type="button" @click="reset()">{{ t('common.reset') }}</button>
            <button type="button" @click="share()">{{ t('common.share') }}</button>
          </div>
        </section>

        <AdSlot placement="leaderboard" />

        <div class="workspace">
          <section class="workspace__list" aria-labelledby="list-heading">
            <h2 id="list-heading" class="workspace__heading">{{ t('list.heading') }}</h2>
            <ProviderPicker
              :provider="provider"
              :options="options"
              @update:provider="changeProvider"
              @update:options="options = $event"
            />
            <template v-if="catalog">
              <FamilyFilter
                :families="catalog.families"
                :selected="selectedFamilies"
                @update="families = $event"
              />
              <InstanceList
                :fits="listFits"
                :sort="sort"
                :interval="interval"
                :hours="hours"
                :primary="primary"
                :secondary="secondary"
                :comparing="comparing"
                :active-slot="activeSlot"
                @select="select"
                @update:sort="sort = $event"
                @update:interval="interval = $event"
              />
            </template>
            <p v-else-if="loadFailed" class="workspace__status" role="alert">
              {{ t('list.loadFailed') }}
            </p>
            <p v-else class="workspace__status">{{ t('common.loading') }}</p>
          </section>

          <section class="workspace__results" aria-labelledby="results-heading">
            <h2 id="results-heading" class="visually-hidden">{{ t('results.heading') }}</h2>
            <div class="slots" :class="{ 'slots--two': slots.length > 1 }">
              <InstanceSummary
                v-for="slot in slots"
                :key="slot.id"
                :fit="slot.fit"
                :slot-id="slot.id"
                :active="activeSlot === slot.id"
                :comparing="comparing"
                :points="slot.points"
                :step="step"
                :interval="interval"
                :hours="hours"
                @activate="activeSlot = slot.id"
                @remove="stopComparing()"
                @update:step="step = $event"
              />
              <p v-if="slots.length === 0" class="slots__empty">{{ t('results.pick') }}</p>
            </div>
            <button
              v-if="!comparing && slots.length"
              type="button"
              class="compare"
              @click="startComparing()"
            >
              {{ t('slot.compare') }}
            </button>
            <CheapestElsewhere
              :entries="elsewhere"
              :hours="hours"
              :interval="interval"
              @compare="compareWith"
            />
          </section>
        </div>

        <p class="sources">{{ sources }} {{ t('sources.disclaimer') }}</p>
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.lead {
  max-width: 48rem;
  margin: var(--space-2) 0 0;
  color: var(--color-text-muted);
}

.panel {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: flex-end;
  justify-content: space-between;
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.panel__actions {
  display: flex;
  gap: var(--space-2);
}

.workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-6);
  align-items: start;
}

@media (min-width: 64rem) {
  .workspace {
    grid-template-columns: minmax(22rem, 30rem) minmax(0, 1fr);
  }
}

.workspace__list,
.workspace__results {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.workspace__heading {
  margin: 0;
  font-size: var(--text-lg);
}

.workspace__status {
  margin: 0;
  color: var(--color-text-muted);
}

.slots {
  display: grid;
  gap: var(--space-4);
}

.slots--two {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
}

.slots__empty {
  margin: 0;
  padding: var(--space-6);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  color: var(--color-text-muted);
  text-align: center;
}

.compare {
  align-self: flex-start;
}

.sources {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

button {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}

.notice {
  font-size: var(--text-sm);
  color: var(--color-success);
}
</style>
